import { Platform } from '../../../types/Platforms';
import { CountReviewBelowMinProduct } from '../../../services/inventory/inventoryService';

/**
 * Shared "order list" grouping: the below-minimum products of a count review, bucketed
 * per supplier. Used by the on-page order-list drawer (`OrderListModal`) and by both
 * exports (Excel order-list sheet, .txt order-list section) so all three show the same
 * suppliers in the same order.
 */

/** Group key for entries without a platform (customs / unknown supplier) — always last. */
export const ORDER_LIST_OTHER_KEY = '__other__';

/**
 * Every platform enum value — the lookup for free-text custom supplier names.
 * `Platform` merges an enum with a namespace, so non-string members are filtered out.
 */
const PLATFORM_VALUES = new Set<string>(Object.values(Platform).filter((v): v is Platform => typeof v === 'string'));

/**
 * Custom ingredients have no platform, only a free-text supplier name. When that name is
 * exactly a platform enum value, group them under that real supplier instead of dropping
 * them into "other / unknown". Anything else stays unknown.
 */
export function resolveOrderListPlatform(supplier: { platform: string | null; name: string | null }): string | null {
  if (supplier.platform) return supplier.platform;
  const name = supplier.name?.trim().toLowerCase();
  return name && PLATFORM_VALUES.has(name) ? name : null;
}

export interface OrderListSupplierGroup {
  key: string;
  platform: string | null;
  /** Capitalised platform id; empty for the "other / unknown" bucket (caller translates). */
  label: string;
  products: CountReviewBelowMinProduct[];
}

/**
 * Bucket below-minimum products per supplier: platform groups alphabetically, the
 * "other / unknown" bucket always last.
 */
export function groupBelowMinBySupplier(products: CountReviewBelowMinProduct[]): OrderListSupplierGroup[] {
  const byKey = new Map<string, OrderListSupplierGroup>();
  for (const p of products) {
    const platform = resolveOrderListPlatform(p.supplier);
    const key = platform ?? ORDER_LIST_OTHER_KEY;
    let g = byKey.get(key);
    if (!g) {
      g = {
        key,
        platform,
        label: platform ? platform.charAt(0).toUpperCase() + platform.slice(1) : '',
        products: [],
      };
      byKey.set(key, g);
    }
    g.products.push(p);
  }
  return Array.from(byKey.values()).sort((a, b) => {
    if (a.key === ORDER_LIST_OTHER_KEY) return 1;
    if (b.key === ORDER_LIST_OTHER_KEY) return -1;
    return a.label.localeCompare(b.label);
  });
}
